/**
 * How a task list behaves — tasks plan T0.2 (docs/tasks-plan/README.md).
 * One answer for the phone's `TaskList`, the desk's Lists, the Today card
 * and the deep link, so no screen re-derives an order or a group: the desk
 * and the phone had each written the fold their own way once already
 * (UI-audit a3-14), and this plan adds four more readers.
 *
 * Dependency-free beyond `taskFold.ts` (itself import-free) so the root
 * suite loads it straight; `dayOf` is passed in rather than imported so
 * nothing here knows a zone.
 */
import { foldTasks as foldLines } from './taskFold.ts'

export interface TaskLine {
  id: string
  parent_id: string | null
  position: number
  done_at: string | null
  due_on: string | null
}

/** D1: how long a ticked row stays on screen, struck through, before it
 *  leaves. One number for every surface. The tick itself is written at
 *  once; this is paint, not data. */
export const LINGER_MS = 900

/** The due `Tag`'s words and tone: "Today" in `--warn`, "Overdue" in
 *  `--bad`, else the date itself — never `toLocaleDateString`, which puts a
 *  comma after the weekday in `en-GB` ("Fri, 3 Oct") that the brief's mock
 *  does not have. Was `TaskList.tsx`'s; D5 gives the desk the same words. */
export type DueTone = 'warn' | 'bad' | 'neutral'
const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export function dueLabel(due_on: string, today: string): { text: string; tone: DueTone } {
  if (due_on === today) return { text: 'Today', tone: 'warn' }
  if (due_on < today) return { text: 'Overdue', tone: 'bad' }
  return { text: shortDay(due_on), tone: 'neutral' }
}

/** "Fri 24 Oct" for a `YYYY-MM-DD`. */
export function shortDay(day: string): string {
  const d = new Date(`${day}T12:00:00Z`)
  return `${WEEKDAY[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH[d.getUTCMonth()]}`
}

/** D4: a line the due-sort pins above the manual order — overdue or due
 *  today. The drag hook offers these no drop target, so a drag never
 *  silently re-dates one. */
export function pinnedByDue(line: Pick<TaskLine, 'due_on' | 'done_at'>, today: string): boolean {
  return !line.done_at && !!line.due_on && line.due_on <= today
}

/** D4: open lines in display order — overdue (oldest due first), then due
 *  today, then by position. A future-dated line keeps its position. */
export function openOrder<T extends TaskLine>(open: readonly T[], today: string): T[] {
  const rank = (l: T) => (pinnedByDue(l, today) ? 0 : 1)
  return [...open].sort((a, b) => {
    const ra = rank(a), rb = rank(b)
    if (ra !== rb) return ra - rb
    if (ra === 0 && a.due_on !== b.due_on) return (a.due_on as string) < (b.due_on as string) ? -1 : 1
    return a.position - b.position
  })
}

export type DoneGroupKey = 'today' | 'yesterday' | 'earlier'
export interface DoneGroup<T> { key: DoneGroupKey; label: string; lines: T[] }

/** D3: a done line is hidden once Clear has been pressed after it. */
export function isCleared(line: Pick<TaskLine, 'done_at'>, clearedAt: string | null | undefined): boolean {
  return !!line.done_at && !!clearedAt && line.done_at < clearedAt
}

/** D3: done lines not cleared, grouped by the day they were done in the
 *  current region (`dayOf`), newest first within each group. Groups with
 *  nothing in them are absent, not empty. */
export function doneGroups<T extends TaskLine>(
  done: readonly T[], clearedAt: string | null | undefined, today: string, dayOf: (iso: string) => string,
): DoneGroup<T>[] {
  const yesterday = addDaysIso(today, -1)
  const groups: Record<DoneGroupKey, T[]> = { today: [], yesterday: [], earlier: [] }
  for (const l of done) {
    if (!l.done_at || isCleared(l, clearedAt)) continue
    const day = dayOf(l.done_at)
    groups[day === today ? 'today' : day === yesterday ? 'yesterday' : 'earlier'].push(l)
  }
  const byDoneDesc = (a: T, b: T) => ((a.done_at as string) < (b.done_at as string) ? 1 : (a.done_at as string) > (b.done_at as string) ? -1 : 0)
  const label: Record<DoneGroupKey, string> = { today: 'Today', yesterday: 'Yesterday', earlier: 'Earlier' }
  return (['today', 'yesterday', 'earlier'] as const)
    .filter(k => groups[k].length)
    .map(k => ({ key: k, label: label[k], lines: groups[k].sort(byDoneDesc) }))
}

/** D3: Clear is offered only when the fold holds a done line older than
 *  today that is not already cleared. */
export function canClear(
  done: readonly TaskLine[], clearedAt: string | null | undefined, today: string, dayOf: (iso: string) => string,
): boolean {
  return done.some(l => !!l.done_at && !isCleared(l, clearedAt) && dayOf(l.done_at) < today)
}

/** D6: the next due day for a ticked repeating task — from its own due day
 *  when that is today or later, else from the day it was done. Noon-UTC
 *  arithmetic, so no DST drift (as `dueParse`'s `D`). */
export function nextDue(due_on: string | null, doneDay: string, every: number, unit: 'day' | 'week'): string {
  const from = due_on && due_on >= doneDay ? due_on : doneDay
  return addDaysIso(from, every * (unit === 'week' ? 7 : 1))
}

/** `YYYY-MM-DD` ± n days, pinned to noon UTC. */
export function addDaysIso(day: string, n: number): string {
  return new Date(Date.parse(`${day}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10)
}

export interface TaskFold<T> {
  /** Open lines on show, in `openOrder`, each with its subtasks under it. */
  top: { item: T; children: T[] }[]
  /** The Done fold, by day, newest first (D3). */
  done: DoneGroup<T>[]
  /** The "Done · N" count: every done line not cleared, a lingering one
   *  included — the count reads `done_at` and is right from the tap (D1),
   *  while the groups wait for the row to leave. */
  doneCount: number
}

/**
 * The fold both screens draw. `foldTasks`'s own rule (a3-14) is kept word
 * for word by delegating to it: an open subtask of a done parent stands at
 * the top, and an open line is never hidden. A line in `lingering` is one
 * ticked within `LINGER_MS`: it is done in the data and still drawn open,
 * in its place, struck through — the one place two readers read `done_at`
 * differently, on purpose, and only for under a second.
 */
export function foldTasks<T extends TaskLine>(
  items: readonly T[],
  opts: { today: string; clearedAt: string | null | undefined; dayOf: (iso: string) => string; lingering?: ReadonlySet<string> },
): TaskFold<T> {
  const lingering = opts.lingering ?? new Set<string>()
  const isDone = (l: T) => !!l.done_at && !lingering.has(l.id)
  const f = foldLines(items, isDone)
  const order = openOrder(f.top.map(t => t.item), opts.today)
  const children = new Map(f.top.map(t => [t.item.id, [...t.children].sort((a, b) => a.position - b.position)]))
  const groups = doneGroups(f.done, opts.clearedAt, opts.today, opts.dayOf)
  return {
    top: order.map(item => ({ item, children: children.get(item.id) ?? [] })),
    done: groups,
    doneCount: items.filter(l => !!l.done_at && !isCleared(l, opts.clearedAt)).length,
  }
}

/** D9 / D10: the list a task goes to when no list was named — the first
 *  active list of kind `task` in the sidebar's order (pinned first, then
 *  position). The Today card and the deep link agree by calling this. */
export function primaryTaskList<L extends { id: string; kind: string; pinned: boolean; position: number; archived_at: string | null }>(lists: readonly L[]): L | null {
  return [...lists]
    .filter(l => l.kind === 'task' && !l.archived_at)
    .sort((a, b) => Number(b.pinned) - Number(a.pinned) || a.position - b.position)[0] ?? null
}
